/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useLocation } from "react-router-dom";
import { Customer } from "~/components/BasicCustomerInfo/types";
import { navItems } from "~/constants";
import { ROUTES } from "~/routing";
import { useAxios, useDetailsNotFound } from "~/tools";

type CustomerProviderProps = {
  customer: Customer;
  setId: React.Dispatch<React.SetStateAction<string>>;
  isCustomerLoading: boolean;
  setCustomer: React.Dispatch<React.SetStateAction<Customer>>;
  fetchCustomer: () => Promise<void>;
};

type IProps = {
  children: React.ReactNode;
};

export const CustomerInitialState: Customer = {
  billingRules: {
    baseManagementFee: "",
    hoursIncludedInFixedFee: {
      hoursOfCsm: "",
      hoursOfMcEngineer: "",
    },
    incidents: {
      billingGranularity: "",
      officeHours: {
        defaultPricePerHour: "",
      },
      outsideOfficeHoursAfterMidnight: {
        defaultPricePerHour: "",
      },
      outsideOfficeHoursBeforeMidnight: {
        defaultPricePerHour: "",
      },
    },
    serviceRequests: {
      billingGranularity: "",
      officeHours: {
        defaultPricePerHour: "",
      },
      outsideOfficeHoursAfterMidnight: {
        defaultPricePerHour: "",
      },
      outsideOfficeHoursBeforeMidnight: {
        defaultPricePerHour: "",
      },
    },
  },
  id: "",
  info: {
    businessHours: {
      start: "",
      end: "",
    },
    businessId: "",
    salesforceId: "",
    categories: [""],
    contract: {
      durationType: "",
      link: "",
      ends: "",
      signed: "",
      starts: "",
    },
    currency: "",
    links: [],
    providers: [],
    premiumSupportCSPs: [""],
    onboardingDate: null,
    offboardingDate: null,
    ibmCustomer: false,
    segment: "",
    cdt: null,
    mcTeams: null,
  },
  location: {
    address: "",
    country: "",
    languages: [""],
    location: "",
  },
  log: {
    created: {
      author: "",
      time: "",
    },
    modified: {
      author: "",
      time: "",
    },
  },
  name: "",
};

export function createCtx<ContextType>() {
  const ctx = React.createContext<ContextType | undefined>(undefined);

  function useCtx() {
    const c = React.useContext(ctx);
    if (!c) throw new Error("useCtx must be inside a Provider with a value");

    return c;
  }

  return [useCtx, ctx.Provider] as const;
}

export const [useCustomer, CtxProvider] = createCtx<CustomerProviderProps>();

export function CustomerProvider({ children }: IProps) {
  const [customer, setCustomer] = React.useState<Customer>(
    CustomerInitialState
  );
  const [isCustomerLoading, setIsCustomerLoading] = React.useState(true);

  const [id, setId] = React.useState("");

  const { detailsNotFound } = useDetailsNotFound();
  const { Axios } = useAxios();

  const { pathname } = useLocation();
  const splitPathname = pathname.split("/");
  const isOnCustomerLevel = splitPathname[1] === "customers";
  const customerId = isOnCustomerLevel && splitPathname[2];
  const mounted = React.useRef(false);

  React.useEffect(() => {
    customerId && setId(customerId);
  }, [customerId]);

  const fetchCustomer = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/customers/${id}`);
      setCustomer(data);
    } catch (e) {
      detailsNotFound(ROUTES.customers.index, navItems.customers);
    } finally {
      mounted.current && setIsCustomerLoading(false);
    }
  }, [Axios, detailsNotFound, id]);

  React.useEffect(() => {
    mounted.current = true;
    id && fetchCustomer();

    return () => {
      mounted.current = false;
    };
  }, [fetchCustomer, id]);

  return (
    <CtxProvider
      value={{ customer, setId, setCustomer, fetchCustomer, isCustomerLoading }}
    >
      {children}
    </CtxProvider>
  );
}
